import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Search, LayoutGrid, Rows3, ChevronLeft, ChevronRight, Settings2, CalendarClock, Megaphone, Image as ImageIcon, Film, Bot, AlertOctagon, ArrowUp, ArrowDown } from "lucide-react";
import SocialPostPanel from "./SocialPostPanel";
import SocialSettings from "./SocialSettings";
import SocialCampaigns from "./SocialCampaigns";
import SocialActivity from "./SocialActivity";
import { S, MANROPE, FOCUS, API, auDate } from "./lib";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const NO_FILTERS = { product: "", vertical: "", stream: "", status: "", campaign: "", needs: "" };
const COLUMNS = "92px 104px minmax(0,1fr) 120px 118px 70px";
const SORTS = [["post_id", "Post"], ["post_date", "Date"], ["hook", "Hook"],
  ["vertical", "Vertical"], ["status", "Status"]];
const monthLabel = (m) =>
  new Date(`${m}-01T00:00:00`).toLocaleDateString("en-AU", { month: "long", year: "numeric" });
const shiftMonth = (m, by) => {
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(y, mo - 1 + by, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const Stat = ({ label, value, tone, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`text-left px-3.5 py-2.5 rounded-[9px] transition-colors ${FOCUS} hover:bg-[#FBFAF7]`}
    style={{ background: S.card, border: `1px solid ${S.border}` }}>
    <div className="text-[10px] font-bold uppercase tracking-[0.09em]" style={{ color: S.ink3 }}>{label}</div>
    <div className="mono text-[17px] font-bold" style={{ color: tone || S.ink }}>{value}</div>
  </button>
);

const Tick = ({ on, letter, title }) => (
  <span title={title} className="inline-flex items-center justify-center rounded-[3px]"
    style={{ width: 13, height: 13, fontSize: 8, fontWeight: 800,
      background: on ? S.good : "#EDE9E0", color: on ? "#fff" : S.muted }}>{letter}</span>
);

const Chip = ({ p, onClick }) => {
  const blocked = (p.blocking_flags || []).length > 0;
  return (
    <button onClick={onClick} data-testid={`social-chip-${p.id}`}
      className={`w-full text-left px-1.5 py-1 rounded-[6px] mb-1 transition-opacity ${FOCUS}`}
      style={{ background: p.status === "published" ? S.panel : S.card,
        border: `1px solid ${p.status === "blocked" || blocked || (p.open_gates || []).length ? S.bad : S.divider}`,
        borderLeft: `3px solid ${p.product_colour}`,
        opacity: p.status === "published" ? 0.72 : 1 }}>
      <div className="flex items-center gap-1 mb-0.5">
        <span className="text-[9.5px] font-bold uppercase truncate" style={{ color: S.ink3 }}>
          {p.vertical || p.stream || "—"}
        </span>
        <span className="ml-auto flex items-center gap-[2px]">
          {blocked && <AlertOctagon className="w-[10px] h-[10px]" style={{ color: S.bad }}
            title={(p.blocking_flags || []).map((x) => x.message).join(" ")} />}
          {p.last_actor_kind === "claude" && (
            <Bot className="w-[10px] h-[10px]" style={{ color: S.accentInk }} title="Last changed by Claude" />
          )}
          {p.has_image && <ImageIcon className="w-[9px] h-[9px]" style={{ color: S.ink3 }} />}
          {p.has_video && <Film className="w-[9px] h-[9px]" style={{ color: S.ink3 }} />}
          <Tick on={p.copy_done} letter="C" title="Copy" />
          <Tick on={p.visual_done} letter="V" title="Visual" />
          <Tick on={p.reel_done} letter="R" title="Reel" />
        </span>
      </div>
      <div className="text-[11px] leading-tight line-clamp-2" style={{ color: S.ink }}>
        {p.hook || p.topic || p.post_id}
      </div>
      {p.status === "awaiting_approval" && (
        <div className="text-[9.5px] font-bold uppercase tracking-[0.06em] mt-0.5"
          style={{ color: S.accentInk }}>Awaiting approval</div>
      )}
    </button>
  );
};

export default function SocialCalendar() {
  const today = new Date().toISOString().slice(0, 10);
  const [month, setMonth] = useState(today.slice(0, 7));
  const [view, setView] = useState("grid");
  const [data, setData] = useState({ posts: [], unscheduled: [], settings: null });
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState(NO_FILTERS);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [sort, setSort] = useState({ key: "post_date", dir: "asc" });
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/staff`).then((r) => setStaff(r.data.staff || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };
      if (q.trim()) params.q = q.trim();
      if (view === "grid" && !params.campaign && !params.needs) params.month = month;
      const [p, s] = await Promise.all([
        axios.get(`${API}/posts`, { params }),
        axios.get(`${API}/stats`),
      ]);
      setData(p.data); setStats(s.data);
    } catch { toast.error("Could not load the social calendar"); }
    finally { setLoading(false); }
  }, [filters, q, month, view]);
  useEffect(() => { load(); }, [load]);

  const settings = data.settings || {};
  const weeks = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    const start = new Date(first);
    start.setDate(1 - ((first.getDay() + 6) % 7));
    const out = [];
    for (let w = 0; w < 6; w++) {
      const row = [];
      for (let d = 0; d < 7; d++) {
        const day = new Date(start);
        day.setDate(start.getDate() + w * 7 + d);
        row.push(`${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`);
      }
      out.push(row);
      const last = new Date(start);
      last.setDate(start.getDate() + w * 7 + 6);
      if (last.getMonth() > m - 1 || last.getFullYear() > y) break;
    }
    return out;
  }, [month]);

  const byDate = useMemo(() => {
    const map = {};
    (data.posts || []).forEach((p) => {
      const d = (p.post_date || "").slice(0, 10) || "unscheduled";
      (map[d] = map[d] || []).push(p);
    });
    return map;
  }, [data.posts]);

  const unscheduled = data.unscheduled || [];

  const sortField = (p) => ({
    post_id: p.post_id || "",
    post_date: (p.post_date || "").slice(0, 10),
    hook: (p.hook || p.topic || "").toLowerCase(),
    vertical: (p.vertical || "").toLowerCase(),
    status: p.status || "",
  }[sort.key] ?? "");

  const rows = useMemo(() => {
    const out = [...(data.posts || [])];
    out.sort((a, b) => {
      const av = sortField(a), bv = sortField(b);
      // A slot with no date sits at the bottom whichever way the column sorts.
      if (sort.key === "post_date" && (!av || !bv)) return !av && !bv ? 0 : (!av ? 1 : -1);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return out;
  }, [data.posts, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortBy = (key) => setSort((s) =>
    s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });

  const addOn = (d) => setOpen({ draft: true, post_date: d, status: "draft" });
  const sel = (key) => (
    <select key={key} value={filters[key]} onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}
      data-testid={`social-filter-${key}`}
      className="h-9 rounded-[7px] px-2 text-[12.5px] bg-white" style={{ border: `1px solid ${S.border}`, color: S.ink2 }}>
      <option value="">{{ product: "All products", vertical: "All verticals", stream: "All streams",
        status: "Any status", campaign: "All campaigns", needs: "Everything" }[key]}</option>
      {(key === "product" ? (settings.products || []).map((p) => [p.key, p.label])
        : key === "status" ? (data.statuses || []).map((s) => [s, (data.status_labels || {})[s] || s])
        : key === "campaign" ? (stats.campaigns || []).map((c) => [c.name, `${c.name} (${c.posts})`])
        : key === "needs" ? [["copy", "Waiting on copy"], ["visual", "Waiting on a visual"],
          ["reel", "Waiting on a reel"], ["approval", "Waiting on approval"],
          ["flagged", "Breaking a house rule"]]
        : (settings[key === "vertical" ? "verticals" : "streams"] || []).map((v) => [v, v])
      ).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );

  return (
    <div className="space-y-3.5" data-testid="social-calendar">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 style={{ ...MANROPE, fontSize: 25, fontWeight: 800, color: S.ink, letterSpacing: "-0.018em" }}>
            Social Calendar
          </h1>
          <p className="text-[13px]" style={{ color: S.ink2 }}>
            Organic LinkedIn and Facebook/Instagram. Copy goes out by hand; the live links come back in.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowActivity(true)} data-testid="social-open-activity"
            className={`h-[34px] px-3 rounded-[7px] inline-flex items-center gap-1.5 text-[12px] font-bold uppercase ${FOCUS}`}
            style={{ background: S.card, border: `1px solid ${S.border}`, color: S.ink2, letterSpacing: "0.04em" }}>
            <Bot className="w-3.5 h-3.5" /> What changed
          </button>
          <button onClick={() => setShowSettings(true)} data-testid="social-open-settings"
            className={`h-[34px] px-3 rounded-[7px] inline-flex items-center gap-1.5 text-[12px] font-bold uppercase ${FOCUS}`}
            style={{ background: S.card, border: `1px solid ${S.border}`, color: S.ink2, letterSpacing: "0.04em" }}>
            <Settings2 className="w-3.5 h-3.5" /> Settings &amp; import
          </button>
          <button onClick={() => addOn(today)} data-testid="social-add-post"
            className={`h-[34px] px-3.5 rounded-[7px] inline-flex items-center gap-1.5 text-[12px] font-bold uppercase ${FOCUS}`}
            style={{ background: S.accent, border: `1px solid ${S.accentEdge}`, color: "#000", letterSpacing: "0.04em" }}>
            <Plus className="w-3.5 h-3.5" /> New post
          </button>
        </div>
      </div>

      <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(132px, 1fr))" }}>
        <Stat label="Slots" value={stats.total ?? "—"} testid="social-stat-total"
          onClick={() => { setFilters(NO_FILTERS); setQ(""); }} />
        <Stat label="Ready to go" value={stats.ready ?? 0} tone={S.good} testid="social-stat-ready"
          onClick={() => setFilters({ ...filters, status: "ready", needs: "" })} />
        <Stat label="Waiting on a visual" value={stats.needs_visual ?? 0} tone={S.accentInk}
          testid="social-stat-visual" onClick={() => setFilters({ ...filters, needs: "visual", status: "" })} />
        <Stat label="Waiting on approval" value={stats.needs_approval ?? 0} tone={S.accentInk}
          testid="social-stat-approval" onClick={() => setFilters({ ...filters, needs: "approval", status: "" })} />
        <Stat label="Awaiting sign-off" value={stats.awaiting_approval ?? 0} tone={S.accentInk}
          testid="social-stat-awaiting"
          onClick={() => setFilters({ ...filters, status: "awaiting_approval", needs: "" })} />
        <Stat label="Breaking a rule" value={stats.flagged ?? 0} tone={S.bad} testid="social-stat-flagged"
          onClick={() => setFilters({ ...filters, needs: "flagged", status: "" })} />
        <Stat label="No date yet" value={stats.unscheduled ?? 0} tone={S.ink2} testid="social-stat-unscheduled" />
        <Stat label="Overdue" value={stats.overdue ?? 0} tone={S.bad} testid="social-stat-overdue" />
        <Stat label="Published" value={stats.published ?? 0} testid="social-stat-published"
          onClick={() => setFilters({ ...filters, status: "published", needs: "" })} />
      </div>

      <div className="rounded-[11px] overflow-hidden" style={{ background: S.card, border: `1px solid ${S.border}` }}>
        <div className="flex items-center gap-2 flex-wrap px-3.5 py-3" style={{ background: S.panel, borderBottom: `1px solid ${S.border}` }}>
          <button onClick={() => setMonth(shiftMonth(month, -1))} data-testid="social-prev-month"
            className={`w-8 h-8 rounded-[7px] flex items-center justify-center ${FOCUS}`} style={{ border: `1px solid ${S.border}`, background: S.card }}>
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-[13.5px] font-bold min-w-[150px]" style={{ ...MANROPE, color: S.ink }} data-testid="social-month">
            {monthLabel(month)}
          </span>
          <button onClick={() => setMonth(shiftMonth(month, 1))} data-testid="social-next-month"
            className={`w-8 h-8 rounded-[7px] flex items-center justify-center ${FOCUS}`} style={{ border: `1px solid ${S.border}`, background: S.card }}>
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: S.muted }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the copy…"
              data-testid="social-search"
              className="h-9 w-[200px] rounded-[7px] pl-8 pr-2 text-[12.5px] bg-white"
              style={{ border: `1px solid ${S.border}`, color: S.ink }} />
          </div>
          {["product", "vertical", "stream", "status", "campaign", "needs"].map((k) => sel(k))}
          <div className="ml-auto flex items-center gap-1">
            {[["grid", LayoutGrid], ["list", Rows3], ["campaigns", Megaphone]].map(([v, Icon]) => (
              <button key={v} onClick={() => setView(v)} data-testid={`social-view-${v}`}
                className={`w-8 h-8 rounded-[7px] flex items-center justify-center ${FOCUS}`}
                style={{ border: `1px solid ${view === v ? S.accentEdge : S.border}`,
                  background: view === v ? "#FFF8D6" : S.card }}>
                <Icon className="w-4 h-4" style={{ color: S.ink2 }} />
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="px-4 py-10 text-[13px]" style={{ color: S.ink2 }}>Loading…</div>
        ) : view === "grid" ? (
          <>
            <div className="overflow-x-auto">
              <div style={{ minWidth: 980 }}>
                <div className="grid" style={{ gridTemplateColumns: "repeat(7, 1fr)" }}>
                  {DAYS.map((d) => (
                    <div key={d} className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.09em]"
                      style={{ color: S.ink3, borderBottom: `1px solid ${S.divider}` }}>{d}</div>
                  ))}
                </div>
                {weeks.map((week, wi) => (
                  <div key={wi} className="grid" style={{ gridTemplateColumns: "repeat(7, 1fr)" }}>
                    {week.map((d) => {
                      const outside = !d.startsWith(month);
                      return (
                        <div key={d} data-testid={`social-day-${d}`} onClick={() => addOn(d)}
                          className="px-1.5 py-1.5 cursor-pointer hover:bg-[#FBFAF7]"
                          style={{ minHeight: 108, borderRight: `1px solid ${S.divider}`,
                            borderBottom: `1px solid ${S.divider}`, background: outside ? S.panel : S.card }}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="mono text-[10.5px]" style={{ color: d === today ? S.accentInk : S.muted,
                              fontWeight: d === today ? 800 : 500 }}>{d.slice(8)}</span>
                          </div>
                          <div onClick={(e) => e.stopPropagation()}>
                            {(byDate[d] || []).map((p) => <Chip key={p.id} p={p} onClick={() => setOpen(p)} />)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
            {unscheduled.length > 0 && (
              <div className="px-3.5 py-3" style={{ background: S.panel, borderTop: `1px solid ${S.border}` }}
                data-testid="social-unscheduled">
                <div className="flex items-center gap-1.5 mb-2">
                  <CalendarClock className="w-3.5 h-3.5" style={{ color: S.ink3 }} />
                  <span className="text-[10px] font-bold uppercase tracking-[0.09em]" style={{ color: S.ink3 }}>
                    No date yet · {unscheduled.length}
                  </span>
                  <span className="text-[11.5px]" style={{ color: S.ink3 }}>
                    — open one and give it a date to drop it onto the grid
                  </span>
                </div>
                <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))" }}>
                  {unscheduled.map((p) => <Chip key={p.id} p={p} onClick={() => setOpen(p)} />)}
                </div>
              </div>
            )}
          </>
        ) : view === "campaigns" ? (
          <SocialCampaigns onOpenCampaign={(name) => {
            setFilters({ ...NO_FILTERS, campaign: name });
            setView("list");
          }} />
        ) : (
          <div>
            {(data.posts || []).length === 0 ? (
              <div className="px-4 py-10 text-[13px]" style={{ color: S.ink2 }} data-testid="social-empty">
                Nothing here yet. Click a day in the calendar, or add a post.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div style={{ minWidth: 700 }}>
                <div className="px-3.5 py-2 grid gap-3 items-center sticky top-0 z-[1]"
                  style={{ gridTemplateColumns: COLUMNS, background: S.panel,
                    borderBottom: `1px solid ${S.border}`, borderLeft: "3px solid transparent" }}
                  data-testid="social-list-header">
                  {SORTS.map(([key, label]) => (
                    <button key={key} onClick={() => sortBy(key)} data-testid={`social-sort-${key}`}
                      className={`text-left text-[10px] font-bold uppercase tracking-[0.09em] inline-flex items-center gap-1 ${FOCUS}`}
                      style={{ color: sort.key === key ? S.ink : S.ink3 }}>
                      {label}
                      {sort.key === key && (sort.dir === "asc"
                        ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                    </button>
                  ))}
                  <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-right"
                    style={{ color: S.ink3 }}>C V R</span>
                </div>
                {rows.map((p) => (
              <button key={p.id} onClick={() => setOpen(p)} data-testid={`social-row-${p.id}`}
                className="w-full text-left px-3.5 py-2.5 grid gap-3 items-center hover:bg-[#FBFAF7]"
                style={{ gridTemplateColumns: COLUMNS,
                  borderBottom: `1px solid ${S.divider}`, borderLeft: `3px solid ${p.product_colour}` }}>
                <span className="mono text-[11px]" style={{ color: S.ink2 }}>{p.post_id}</span>
                <span className="text-[11.5px]" style={{ color: p.overdue ? S.bad : S.ink2 }}>
                  {auDate(p.post_date)}
                </span>
                <span className="text-[12.5px] truncate" style={{ color: S.ink }}>
                  {p.hook || p.topic || <span style={{ color: S.muted }}>No hook yet</span>}
                </span>
                <span className="text-[11.5px] truncate" style={{ color: S.ink2 }}>{p.vertical || "—"}</span>
                <span className="text-[11px] font-bold uppercase" style={{ color: p.status === "blocked" ? S.bad : S.accentInk }}>
                  {(data.status_labels || {})[p.status] || p.status}
                  {(p.blocking_flags || []).length > 0 && (
                    <AlertOctagon className="w-3 h-3 inline ml-1" style={{ color: S.bad }} />
                  )}
                </span>
                <span className="flex items-center gap-[3px] justify-end">
                  <Tick on={p.copy_done} letter="C" title="Copy" />
                  <Tick on={p.visual_done} letter="V" title="Visual" />
                  <Tick on={p.reel_done} letter="R" title="Reel" />
                </span>
              </button>
                ))}
                </div>
              </div>
            )}
            <div className="px-3.5 py-2 text-[11.5px]" style={{ color: S.ink3 }}>
              {(data.posts || []).length} of {stats.total ?? 0} slots
            </div>
          </div>
        )}
      </div>

      {open && <SocialPostPanel post={open} settings={settings} statuses={data.statuses || []}
        statusLabels={data.status_labels || {}} staff={staff} onClose={() => setOpen(null)}
        onSaved={() => { setOpen(null); load(); }} />}
      {showSettings && <SocialSettings onClose={() => setShowSettings(false)} onSaved={load} />}
      {showActivity && (
        <SocialActivity onClose={() => setShowActivity(false)}
          onOpenPost={async (uid) => {
            try {
              const r = await axios.get(`${API}/posts/${uid}`);
              setShowActivity(false); setOpen(r.data.post);
            } catch { toast.error("That post is no longer here"); }
          }} />
      )}
    </div>
  );
}
